<template>
<div>
    <!-- HERO -->
    <section class="pt-12 pt-md-14 mt-n11 bg-primary">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-8">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb breadcrumb-dark mb-2">
                            <li class="breadcrumb-item"><router-link to="/docs" class="text-white-80">Docs</router-link></li>
                            <li class="breadcrumb-item active text-white" aria-current="page">MCP Server</li>
                        </ol>
                    </nav>
                    <h1 class="display-3 fw-bold text-white">MCP Server Documentation</h1>
                    <p class="lead text-white-80 mb-0">
                        Model Context Protocol server for AI-powered Instagram data analysis with Claude.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- CONTENT -->
    <section class="py-8 py-md-11">
        <div class="container">
            <div class="row">
                <!-- SIDEBAR NAV -->
                <div class="col-12 col-lg-3 mb-8 mb-lg-0">
                    <div class="docs-nav-sidebar">
                        <h6 class="text-uppercase text-muted mb-3">On this page</h6>
                        <nav class="nav flex-column">
                            <a href="#overview" class="nav-link px-0">Overview</a>
                            <a href="#architecture" class="nav-link px-0">Architecture</a>
                            <a href="#setup" class="nav-link px-0">Setup</a>
                            <a href="#tools" class="nav-link px-0">Available Tools</a>
                            <a href="#examples" class="nav-link px-0">Example Prompts</a>
                            <a href="#advanced" class="nav-link px-0">Advanced</a>
                        </nav>
                    </div>
                </div>

                <!-- MAIN CONTENT -->
                <div class="col-12 col-lg-9">
                    <!-- OVERVIEW -->
                    <div id="overview" class="docs-section">
                        <h2 class="fw-bold">Overview</h2>
                        <p class="text-muted fs-lg">
                            MCP (Model Context Protocol) allows AI assistants like Claude to directly interact
                            with external data sources. Our MCP server provides Claude with tools to query
                            Instagram data using natural language.
                        </p>
                        <div class="row mt-4">
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">What you can do</h5>
                                        <ul class="text-muted mb-0">
                                            <li>Ask Claude to find Instagram profiles</li>
                                            <li>Analyze engagement and follower data</li>
                                            <li>Research hashtags and trends</li>
                                            <li>Find influencers by demographics</li>
                                            <li>Run custom SQL queries</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Supported clients</h5>
                                        <ul class="text-muted mb-0">
                                            <li>Claude.ai (Web)</li>
                                            <li>Claude Desktop (Mac/Windows)</li>
                                            <li>Any MCP-compatible client</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ARCHITECTURE -->
                    <div id="architecture" class="docs-section">
                        <h2 class="fw-bold">Architecture</h2>
                        <p class="text-muted">
                            MCP uses Streamable HTTP transport for efficient communication between Claude and our data.
                        </p>
                        <div class="docs-diagram mt-4">
                            <pre class="mb-0 text-white-80">
┌─────────────────────────────────────────────────────────────────┐
│                        Claude Desktop / Claude.ai                │
│                                                                  │
│  "Find top fitness influencers in New York with 10K+ followers" │
└───────────────────────────────┬──────────────────────────────────┘
                                │
                                │ HTTP (Streamable)
                                ▼
┌───────────────────────────────────────────────────────────────────┐
│                     MCP Server (mcp.datalikers.com)               │
│                                                                   │
│  ┌─────────────┐  ┌─────────────┐  ┌─────────────┐               │
│  │ Tool:       │  │ Tool:       │  │ Tool:       │               │
│  │ search_users│  │ get_user_by │  │ get_stats   │  ... 27 tools │
│  │             │  │ _username   │  │             │               │
│  └──────┬──────┘  └──────┬──────┘  └──────┬──────┘               │
│         │                │                │                       │
│         └────────────────┼────────────────┘                       │
│                          ▼                                        │
│                  ┌───────────────┐                                │
│                  │   Database    │                                │
│                  │ (1.5B+ rows)  │                                │
│                  └───────────────┘                                │
└───────────────────────────────────────────────────────────────────┘</pre>
                        </div>
                    </div>

                    <!-- SETUP -->
                    <div id="setup" class="docs-section">
                        <h2 class="fw-bold">Setup</h2>

                        <!-- Tabs -->
                        <ul class="nav nav-tabs mb-4" role="tablist">
                            <li class="nav-item">
                                <a class="nav-link active" data-bs-toggle="tab" href="#setup-web" role="tab">Claude.ai</a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" data-bs-toggle="tab" href="#setup-desktop" role="tab">Claude Desktop</a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" data-bs-toggle="tab" href="#setup-code" role="tab">Claude Code</a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" data-bs-toggle="tab" href="#setup-cursor" role="tab">Cursor</a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" data-bs-toggle="tab" href="#setup-zed" role="tab">Zed</a>
                            </li>
                        </ul>

                        <div class="tab-content">
                            <!-- Claude.ai Setup -->
                            <div class="tab-pane fade show active" id="setup-web" role="tabpanel">
                                <ol class="text-muted">
                                    <li class="mb-3">
                                        <strong class="text-white">Open Settings</strong><br>
                                        Click on your profile icon in the bottom left corner, then select "Settings"
                                    </li>
                                    <li class="mb-3">
                                        <strong class="text-white">Go to Connectors</strong><br>
                                        In the settings menu, click on "Connectors" tab
                                    </li>
                                    <li class="mb-3">
                                        <strong class="text-white">Add Custom Connector</strong><br>
                                        Click "Add custom connector" button
                                    </li>
                                    <li class="mb-3">
                                        <strong class="text-white">Enter MCP URL</strong><br>
                                        Enter the server URL:
                                        <div class="docs-code-block mt-2">
                                            <code>https://mcp.datalikers.com/mcp/</code>
                                        </div>
                                        Then add Authorization header with your access key:
                                        <div class="docs-code-block mt-2">
                                            <code>Authorization: Bearer {{ accessKey || 'YOUR_ACCESS_KEY' }}</code>
                                            <button class="btn btn-sm btn-outline-primary ms-2" @click="copyMcpUrl">
                                                <i class="fe fe-copy"></i>
                                            </button>
                                        </div>
                                    </li>
                                    <li class="mb-3">
                                        <strong class="text-white">Save and Start Using</strong><br>
                                        Click "Save" and start a new conversation. Claude now has access to Instagram data tools.
                                    </li>
                                </ol>
                            </div>

                            <!-- Claude Desktop Setup -->
                            <div class="tab-pane fade" id="setup-desktop" role="tabpanel">
                                <p class="text-muted">
                                    Add the following to your Claude Desktop configuration file:
                                </p>

                                <h6 class="mt-4">Config file location:</h6>
                                <ul class="text-muted small">
                                    <li><strong>macOS:</strong> <code>~/Library/Application Support/Claude/claude_desktop_config.json</code></li>
                                    <li><strong>Windows:</strong> <code>%APPDATA%\Claude\claude_desktop_config.json</code></li>
                                    <li><strong>Linux:</strong> <code>~/.config/Claude/claude_desktop_config.json</code></li>
                                </ul>

                                <h6 class="mt-4">Configuration:</h6>
                                <div class="docs-code-block">
                                    <pre><code>{
  "mcpServers": {
    "datalikers": {
      "url": "https://mcp.datalikers.com/mcp/",
      "headers": {
        "Authorization": "Bearer {{ accessKey || 'YOUR_ACCESS_KEY' }}"
      }
    }
  }
}</code></pre>
                                </div>

                                <p class="text-muted mt-3">
                                    After saving the config, restart Claude Desktop to apply changes.
                                </p>
                            </div>

                            <!-- Claude Code Setup -->
                            <div class="tab-pane fade" id="setup-code" role="tabpanel">
                                <p class="text-muted">
                                    Add DataLikers MCP server with a single command:
                                </p>
                                <div class="docs-code-block">
                                    <pre><code>claude mcp add \
  --transport http \
  --header "Authorization: Bearer {{ accessKey || 'YOUR_ACCESS_KEY' }}" \
  -s project \
  datalikers https://mcp.datalikers.com/mcp/</code></pre>
                                </div>
                                <h6 class="mt-4">Scope options:</h6>
                                <ul class="text-muted small">
                                    <li><code>-s project</code> — available only in current project</li>
                                    <li><code>-s user</code> — available globally for all projects</li>
                                </ul>
                            </div>

                            <!-- Cursor Setup -->
                            <div class="tab-pane fade" id="setup-cursor" role="tabpanel">
                                <p class="text-muted">
                                    Open Cursor Settings → Features → MCP Servers, or edit:
                                </p>
                                <ul class="text-muted small">
                                    <li><strong>macOS/Linux:</strong> <code>~/.cursor/mcp.json</code></li>
                                    <li><strong>Windows:</strong> <code>%USERPROFILE%\.cursor\mcp.json</code></li>
                                </ul>
                                <div class="docs-code-block">
                                    <pre><code>{
  "mcpServers": {
    "datalikers": {
      "url": "https://mcp.datalikers.com/mcp/",
      "headers": {
        "Authorization": "Bearer {{ accessKey || 'YOUR_ACCESS_KEY' }}"
      }
    }
  }
}</code></pre>
                                </div>
                            </div>

                            <!-- Zed Setup -->
                            <div class="tab-pane fade" id="setup-zed" role="tabpanel">
                                <p class="text-muted">
                                    Add to your Zed settings file:
                                </p>
                                <ul class="text-muted small">
                                    <li><strong>macOS/Linux:</strong> <code>~/.config/zed/settings.json</code></li>
                                </ul>
                                <div class="docs-code-block">
                                    <pre><code>{
  "context_servers": {
    "datalikers": {
      "settings": {
        "url": "https://mcp.datalikers.com/mcp/",
        "headers": {
          "Authorization": "Bearer {{ accessKey || 'YOUR_ACCESS_KEY' }}"
        }
      }
    }
  }
}</code></pre>
                                </div>
                            </div>
                        </div>

                        <div class="alert alert-warning mt-4" v-if="!accessKey">
                            <strong>Get your access key:</strong>
                            <router-link :to="{path: '/tokens'}">Create a token</router-link> in your Console to get started.
                        </div>
                    </div>

                    <!-- TOOLS -->
                    <div id="tools" class="docs-section">
                        <h2 class="fw-bold">Available Tools</h2>
                        <p class="text-muted mb-4">
                            Claude can use these tools to query Instagram data:
                        </p>

                        <!-- Users -->
                        <h4 class="mt-4 mb-3">Users</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Tool</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>get_user_by_username</code></td>
                                        <td>Get full profile info for a username</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_user_by_id</code></td>
                                        <td>Get profile by Instagram user ID</td>
                                    </tr>
                                    <tr>
                                        <td><code>search_users</code></td>
                                        <td>Search users by username or full name</td>
                                    </tr>
                                    <tr>
                                        <td><code>search_users_by_demographics</code></td>
                                        <td>Find users by age, gender, race, country, city, follower range</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_users_by_hashtag</code></td>
                                        <td>Get users who used a specific hashtag</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_users_by_location</code></td>
                                        <td>Get users from a specific location/city</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_top_users</code></td>
                                        <td>Get top users by follower count</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_business_users</code></td>
                                        <td>Get business accounts by category</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_user_photo</code></td>
                                        <td>Get and display user's profile photo</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_user_engagement</code></td>
                                        <td>Calculate engagement metrics (avg likes, comments per post)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Posts & Media -->
                        <h4 class="mt-5 mb-3">Posts & Reels</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Tool</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>get_user_medias</code></td>
                                        <td>Get posts/reels by specific user</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_media_by_code</code></td>
                                        <td>Get post/reel by Instagram shortcode</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_top_medias</code></td>
                                        <td>Get top posts by likes or comments</td>
                                    </tr>
                                    <tr>
                                        <td><code>search_media_captions</code></td>
                                        <td>Search posts by caption text</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Comments -->
                        <h4 class="mt-5 mb-3">Comments</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Tool</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>get_comments_by_user</code></td>
                                        <td>Get comments made by a specific user</td>
                                    </tr>
                                    <tr>
                                        <td><code>search_comments</code></td>
                                        <td>Search comments by text</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Hashtags -->
                        <h4 class="mt-5 mb-3">Hashtags</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Tool</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>get_hashtag_info</code></td>
                                        <td>Get hashtag statistics and info</td>
                                    </tr>
                                    <tr>
                                        <td><code>search_hashtags</code></td>
                                        <td>Search hashtags by name</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_top_hashtags</code></td>
                                        <td>Get most popular hashtags by media count</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Locations -->
                        <h4 class="mt-5 mb-3">Locations</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Tool</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>get_location_info</code></td>
                                        <td>Get location details by ID</td>
                                    </tr>
                                    <tr>
                                        <td><code>search_locations</code></td>
                                        <td>Search locations by name or city</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Stories & Highlights -->
                        <h4 class="mt-5 mb-3">Stories & Highlights</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Tool</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>get_user_stories</code></td>
                                        <td>Get stories by specific user</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_story_by_id</code></td>
                                        <td>Get story by ID</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_user_highlights</code></td>
                                        <td>Get highlights by specific user</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_highlight_by_id</code></td>
                                        <td>Get highlight by ID</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Audio Tracks -->
                        <h4 class="mt-5 mb-3">Audio Tracks</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Tool</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>search_tracks</code></td>
                                        <td>Search music tracks by title or artist</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_track_by_id</code></td>
                                        <td>Get music track by ID</td>
                                    </tr>
                                    <tr>
                                        <td><code>get_top_tracks</code></td>
                                        <td>Get popular music tracks</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Stats -->
                        <h4 class="mt-5 mb-3">Statistics</h4>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Tool</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>get_stats</code></td>
                                        <td>Get database statistics (total users, posts, comments, etc.)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- EXAMPLES -->
                    <div id="examples" class="docs-section">
                        <h2 class="fw-bold">Example Prompts</h2>
                        <p class="text-muted mb-4">
                            Once connected, you can ask Claude questions like:
                        </p>

                        <div class="row">
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h6 class="text-primary-desat">User Research</h6>
                                        <ul class="text-muted small mb-0">
                                            <li>"Get profile information for @nike"</li>
                                            <li>"Find fitness influencers with 50K-100K followers"</li>
                                            <li>"Who are the top followers of @cristiano?"</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h6 class="text-primary-desat">Content Analysis</h6>
                                        <ul class="text-muted small mb-0">
                                            <li>"Analyze the last 10 posts from @natgeo"</li>
                                            <li>"What's the engagement rate for @nike?"</li>
                                            <li>"Show top comments on this post"</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h6 class="text-primary-desat">Hashtag Research</h6>
                                        <ul class="text-muted small mb-0">
                                            <li>"How many posts use #fitness?"</li>
                                            <li>"Find trending posts in #travel"</li>
                                            <li>"Compare #marketing vs #digitalmarketing"</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h6 class="text-primary-desat">Location Analysis</h6>
                                        <ul class="text-muted small mb-0">
                                            <li>"Show recent posts from Times Square"</li>
                                            <li>"Find influencers posting from Dubai"</li>
                                            <li>"Analyze content trends in Tokyo"</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ADVANCED -->
                    <div id="advanced" class="docs-section">
                        <h2 class="fw-bold">Advanced Usage</h2>

                        <h5 class="mt-4">Combining Tools</h5>
                        <p class="text-muted">
                            Claude can chain multiple tools together in a single conversation.
                            For example, find users by demographics, then analyze their engagement,
                            and explore their recent posts — all in one request.
                        </p>

                        <h5 class="mt-4">Database Coverage</h5>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Data Type</th>
                                        <th>Description</th>
                                        <th>Records</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>User Profiles (full)</td>
                                        <td>Detailed profiles with bio, contacts, stats</td>
                                        <td>30M+</td>
                                    </tr>
                                    <tr>
                                        <td>User Profiles (short)</td>
                                        <td>Lightweight profiles (username, name, avatar)</td>
                                        <td>945M+</td>
                                    </tr>
                                    <tr>
                                        <td>Posts & Reels</td>
                                        <td>Photos, videos, carousels with captions</td>
                                        <td>192M+</td>
                                    </tr>
                                    <tr>
                                        <td>Comments</td>
                                        <td>Post comments with author info</td>
                                        <td>388M+</td>
                                    </tr>
                                    <tr>
                                        <td>Hashtags</td>
                                        <td>Hashtag metadata and usage stats</td>
                                        <td>22M+</td>
                                    </tr>
                                    <tr>
                                        <td>Locations</td>
                                        <td>Places with coordinates and addresses</td>
                                        <td>4M+</td>
                                    </tr>
                                    <tr>
                                        <td>Audio Tracks</td>
                                        <td>Music used in reels</td>
                                        <td>78K+</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="alert alert-primary mt-4">
                            <strong>Pro tip:</strong> Simply describe what data you need in natural language.
                            Claude will choose the right tools and combine results automatically.
                        </div>
                    </div>

                    <!-- NEXT STEPS -->
                    <div class="mt-5 pt-5 border-top border-secondary">
                        <h4>Next Steps</h4>
                        <div class="row mt-4">
                            <div class="col-12 col-md-6 mb-3">
                                <router-link :to="{path: '/docs/cache-api'}" class="card bg-dark border-0 h-100 text-decoration-none lift">
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">Cache API</h6>
                                        <p class="text-muted small mb-0">Use REST API directly in your code</p>
                                    </div>
                                </router-link>
                            </div>
                            <div class="col-12 col-md-6 mb-3">
                                <router-link :to="{path: '/docs/dataset-builder'}" class="card bg-dark border-0 h-100 text-decoration-none lift">
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">Dataset Builder</h6>
                                        <p class="text-muted small mb-0">Collect custom datasets</p>
                                    </div>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
export default {
    data() {
        return {
            accessKey: null
        }
    },
    created() {
        this.$methods.buildTitle('MCP Server Documentation')
        this.fetchAccessKey()
    },
    mounted() {
        if (this.$route.hash) {
            this.$nextTick(() => {
                const element = document.querySelector(this.$route.hash)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                }
            })
        }
    },
    methods: {
        fetchAccessKey() {
            if (this.$state.context.id) {
                this.$services.Token().get().then(resp => {
                    if (resp.body && resp.body.length > 0) {
                        this.accessKey = resp.body[0].access_key
                    }
                })
            }
        },
        copyMcpUrl() {
            const header = `Bearer ${this.accessKey || 'YOUR_ACCESS_KEY'}`
            this.$methods.copyToClipboard(header)
        }
    }
}
</script>
